import React from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { resolveAsset } from "../utils/paths";
import {
  FaRegHeart,
  FaHandHoldingHeart,
  FaUsers,
  FaWheelchair,
  FaHospital,
  FaPills,
  FaHeartbeat,
  FaBrain,
  FaUserFriends,
  FaQuoteLeft,
  FaLeaf,
} from "react-icons/fa";
import "./Health.css";

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const INITIATIVES = [
  {
    icon: FaHeartbeat,
    title: "Organizing Health Camps",
    text: "We organize health and eye camps in Puri district and 45 slums of Bhubaneswar.",
  },
  {
    icon: FaUsers,
    title: "Community Process Activities",
    text: "Facilitating Community Process Activities by forming Ward Kalyan Samitis (WKS) and Mahila Arogya Samitis (MAS).",
  },
  {
    icon: FaWheelchair,
    title: "Disability Identification & Support",
    text: "Early identification of physical/mental disability, providing post-therapy rehabilitation and assistive devices.",
  },
  {
    icon: FaHospital,
    title: "Medical Referrals & Home Care",
    text: "Referrals to specialty hospitals for medical/surgical interventions, plus Home Based and Institutional Therapy provided to nearly 700 persons with disabilities.",
  },
  {
    icon: FaPills,
    title: "Focus on Treatment & Rehabilitation",
    text: "Focus on eradication, treatment, and rehabilitation of leprosy affected persons.",
  },
];

const Health = () => {
  const { openDonation } = useDonation();

  return (
    <>
      <Header />

      {/* ================= Hero ================= */}
      <section className="hl-page">
        <div className="hl-container">
          <motion.div
            className="hl-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div
              className="hl-hero__image"
              style={{
                backgroundImage: `url("${resolveAsset(
                  "assets/images/backgrounds/Health.jpg"
                )}"), url("${resolveAsset(
                  "build/assets/images/backgrounds/Health.jpg"
                )}")`,
              }}
            />
            <div className="hl-hero__overlay" />

            <div className="hl-hero__content">
              <div className="hl-hero__mark">
                <FaRegHeart className="hl-hero__mark-heart" />
                <FaHandHoldingHeart className="hl-hero__mark-hand" />
                <svg className="hl-hero__squiggle" viewBox="0 0 220 120" fill="none">
                  <path
                    d="M2 2 C 2 40, 2 70, 40 90 S 150 118, 215 108"
                    stroke="#f2622e"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>

              <span className="hl-eyebrow">Our Commitment To</span>
              <motion.h2
                className="hl-hero__title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                Health
              </motion.h2>
              <motion.p
                className="hl-hero__text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                Vikash promotes health initiatives by providing essential
                medical care, resources, and support to enhance the well-being
                of communities in need.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= Health initiatives ================= */}
      <section className="hl-initiatives">
        <Container>
          <Row className="align-items-start g-5">
            <Col lg={5}>
              <motion.div
                className="hl-initiatives__imageWrap"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <img
                  src="/assets/images/sections/health (1)1.jpg"
                  alt="Health Program"
                  className="hl-initiatives__image"
                />
                <div className="hl-initiatives__badge">
                  <span className="hl-initiatives__badge-icon">
                    <FaHeartbeat />
                  </span>
                  <span>
                    Bringing Healthcare
                    <br />
                    Closer to Communities
                  </span>
                </div>
              </motion.div>
            </Col>

            <Col lg={7}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <span className="hl-eyebrow hl-eyebrow--dark">Our Health Initiatives</span>
                <h2 className="hl-heading">
                  Comprehensive Care
                  <br />
                  for a Healthier Tomorrow
                </h2>
                <span className="hl-heading__underline" />
              </motion.div>

              <motion.div
                className="hl-initiativeList"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={staggerContainer}
              >
                {INITIATIVES.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.title}
                      className={`hl-initiativeItem ${i === INITIATIVES.length - 1 ? "hl-initiativeItem--last" : ""}`}
                      variants={staggerItem}
                    >
                      <span className="hl-initiativeItem__icon">
                        <Icon />
                      </span>
                      <div>
                        <h5 className="hl-initiativeItem__title">{item.title}</h5>
                        <p className="hl-initiativeItem__text">{item.text}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ================= Community mental health ================= */}
      <section className="hl-mental">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={5}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <span className="hl-eyebrow hl-eyebrow--dark">
                  Caring For Minds, Building Futures
                </span>
                <h2 className="hl-heading">
                  Community
                  <br />
                  Mental Health
                </h2>
                <span className="hl-heading__underline" />

                <p className="hl-mental__text">
                  We have experienced that people exposed to poverty,
                  unemployment, domestic violence, social exclusion, disaster
                  and pandemics are prone to mental illness. People with mental
                  illness are often excluded from full participation in
                  community life and health care services, being an invisible
                  group.
                </p>
                <p className="hl-mental__text">
                  This program involves awareness, identification, counseling,
                  psychosocial support, medicine, social security programs, and
                  livelihood promotion activities. Our goal is to promote the
                  participation of Persons with Mental Illness (PWMIs) in the
                  mainstream development process.
                </p>
              </motion.div>
            </Col>

            <Col lg={7}>
              <Row className="g-4">
                <Col xs={6}>
                  <motion.div
                    className="hl-mentalCard"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                  >
                    <img
                      src="/assets/images/sections/mental.jpg"
                      alt="Support & Recovery"
                      className="hl-mentalCard__image"
                    />
                    <div className="hl-mentalCard__info">
                      <span className="hl-mentalCard__icon">
                        <FaBrain />
                      </span>
                      <div>
                        <h6>Support & Recovery</h6>
                        <p>Providing care, counseling, and psychosocial support.</p>
                      </div>
                    </div>
                  </motion.div>
                </Col>
                <Col xs={6}>
                  <motion.div
                    className="hl-mentalCard"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                  >
                    <img
                      src="/assets/images/sections/health12.jpg"
                      alt="Awareness & Inclusion"
                      className="hl-mentalCard__image"
                    />
                    <div className="hl-mentalCard__info">
                      <span className="hl-mentalCard__icon">
                        <FaUserFriends />
                      </span>
                      <div>
                        <h6>Awareness & Inclusion</h6>
                        <p>Raising awareness and ensuring inclusion for all.</p>
                      </div>
                    </div>
                  </motion.div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Quote */}
          <motion.div
            className="hl-quote"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <FaLeaf className="hl-quote__leaf hl-quote__leaf--left" />
            <FaQuoteLeft className="hl-quote__mark" />
            <p className="hl-quote__text">
              "Empowering individuals through accessible healthcare builds
              stronger and healthier communities for generations to come."
            </p>
            <FaLeaf className="hl-quote__leaf hl-quote__leaf--right" />
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="hl-cta">
        <Container>
          <motion.div
            className="hl-cta__inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <h2 className="hl-cta__title">You Can Help The Poor With Us</h2>
            <div className="hl-cta__divider">
              <span />
              <FaLeaf />
              <span />
            </div>
            <p className="hl-cta__text">
              Your contribution can make a significant difference. Join us in
              our mission.
            </p>
            <div className="hl-cta__actions">
              <button type="button" className="hl-btn hl-btn--solid" onClick={openDonation}>
                <FaRegHeart /> Donate Now
              </button>
              <Link to="/volunteer" className="hl-btn hl-btn--outline">
                <FaUsers /> Join Us
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default Health;